<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import TeletelMetaStatusAPI from 'dashboard/api/teletel/metaStatus';
import { useTeletelFeatures } from 'dashboard/composables/useTeletelFeatures';

// Red strip above the WhatsApp campaigns when Meta reports account-level
// trouble (payment, verification, token) on any connected number: the
// manager sees why the campaign will fail before pressing "create".
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const problems = ref([]);
const { isTeletelFeatureEnabled } = useTeletelFeatures();

onMounted(async () => {
  if (!isTeletelFeatureEnabled('meta_status')) return;
  try {
    const { data } = await TeletelMetaStatusAPI.show();
    const fromNotices = (data.notices || []).filter(
      n => !n.resolved_at && ['account', 'token'].includes(n.kind)
    );
    const fromHealth = (data.numbers || [])
      .filter(n => n.can_send === 'BLOCKED')
      .flatMap(n =>
        (n.issues || [])
          .filter(i => i.severity === 'blocked')
          .map(i => ({ ...i, inbox_id: n.inbox_id, inbox_name: n.inbox_name }))
      );
    const seen = new Set();
    problems.value = [...fromHealth, ...fromNotices].filter(p => {
      const key = `${p.inbox_id || p.inbox_name}-${p.code}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  } catch (e) {
    problems.value = [];
  }
});

const openMetaPage = () =>
  router.push({
    name: 'teletel_meta',
    params: { accountId: route.params.accountId },
  });
</script>

<template>
  <div
    v-if="problems.length"
    class="mb-3 rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-4 py-3 text-sm text-n-ruby-12"
  >
    <p class="mb-1 font-semibold">{{ t('TELETEL_META.BLOCKED_BANNER') }}</p>
    <ul class="list-disc ps-5">
      <li
        v-for="p in problems"
        :key="`${p.inbox_id || p.inbox_name}-${p.code}`"
      >
        <span v-if="p.inbox_name" class="font-medium">
          {{ p.inbox_name }}
        </span>
        {{ p.title || p.meta_title }}
        <span v-if="p.fix"> — {{ p.fix }}</span>
      </li>
    </ul>
    <button
      type="button"
      class="mt-2 text-sm font-medium underline"
      @click="openMetaPage"
    >
      {{ t('TELETEL_META.HEADER') }}
    </button>
  </div>
</template>
